import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { BlankLayoutComponent } from '@layouts/blank-layout/blank-layout.component';
import { VerticalLayoutComponent } from '@layouts/vertical-layout/vertical-layout.component';
import { NgxSonnerToaster } from 'ngx-sonner';
import { filter, map } from 'rxjs/operators';

import { ScreenLoaderComponent } from '@components/screen-loader/screen-loader.component';

import { ApplicationLoaderService } from '@services/helper/application-loader.service';
import { LocalStorageService } from '@services/helper/local-storage.service';
import { logger } from '@services/helper/logger.service';

/** The page shells a route can pick through `data.layout`. */
type AppLayout = 'private' | 'blank';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NgxSonnerToaster,
    VerticalLayoutComponent,
    BlankLayoutComponent,
    ScreenLoaderComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  providers: [Title],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit, AfterViewInit {
  protected layout = signal<AppLayout | null>('private'); // Default layout
  private readonly _host = inject(ElementRef);
  private readonly _applicationLoaderService = inject(ApplicationLoaderService);
  protected _isLoadingApplication = signal<boolean>(this._applicationLoaderService.isLoading());

  // Inject services
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _localStorageService = inject(LocalStorageService);

  /**
   * Human-readable name of the active page. Rendered into a visually-hidden
   * `aria-live="polite"` region (see app.html) so screen readers announce the
   * new page on SPA navigation — without it, route changes are silent to AT.
   */
  protected pageTitle = signal<string>('');
  private readonly _titleService = inject(Title);

  constructor() {
    this.listenToRouterEvents();
    effect(() => {
      this._isLoadingApplication.set(this._applicationLoaderService.isLoading());
    });
  }

  /**
   * Keep ngx-sonner toasts visible above CDK dialog/popup overlays.
   *
   * Angular CDK (v21+) renders overlays via the native Popover API, which puts
   * them in the browser's **top layer** — a layer that always paints above the
   * normal document REGARDLESS of z-index. A toast in the normal document can
   * therefore never sit above an open dialog, no matter how high its z-index
   * (this is why an earlier z-index/teleport approach had no effect).
   *
   * The only way to compete with the top layer is to join it. We promote the
   * toaster to a manual popover and re-show it whenever a toast is added, so it
   * re-enters the top layer ABOVE whichever overlay is currently open.
   *
   * NOTE: the app now opts CDK overlays OUT of the Popover API entirely
   * (`OVERLAY_DEFAULT_CONFIG` in app.config.ts — it was painting `appendTo`
   * dropdown panels behind open dialogs), so overlays are back to z-index 1000.
   * This promotion is kept: the top layer still out-ranks them, and it keeps
   * working if that opt-out is ever lifted.
   */
  public ngAfterViewInit(): void {
    const toaster = this._host.nativeElement.querySelector(
      'ngx-sonner-toaster',
    ) as HTMLElement | null;
    if (!toaster) return;

    /* Move to <body> so it lives as a clean top-level node outside app-root. */
    if (toaster.parentElement !== document.body) {
      document.body.appendChild(toaster);
    }

    /* Graceful no-op on browsers without the Popover API. */
    if (!('showPopover' in toaster)) return;

    toaster.setAttribute('popover', 'manual');

    /* hide+show re-inserts the popover at the TOP of the top layer. */
    const restack = (): void => {
      try {
        toaster.hidePopover();
      } catch {
        /* not currently open — nothing to hide */
      }
      try {
        toaster.showPopover();
      } catch {
        /* ignore: not connected / already handled */
      }
    };
    restack();

    /* Re-stack above the current top layer each time a toast is added. */
    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.addedNodes.length > 0)) restack();
    });
    observer.observe(toaster, { childList: true, subtree: true });
  }

  public ngOnInit(): void {
    const isFirstTimeLogin = !this._localStorageService.GetItem(
      this._localStorageService.IS_REGULAR_USER,
    );
    logger.info('isFirstTimeLogin', isFirstTimeLogin);
    if (isFirstTimeLogin) {
      setTimeout(() => {
        this._localStorageService.SetItem(this._localStorageService.IS_REGULAR_USER, 'true');
      }, 1000);
    }
  }

  /**
   * Listen to router events to update layout.
   *
   * `layout` and `pageTitle` are signals, so writing them is what schedules the
   * OnPush re-render — no manual `detectChanges()` is needed here.
   */
  private listenToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.findDeepestRouteLayout()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((layout) => {
        this.layout.set(layout);
        this.announceRouteChange();
      });
  }

  /** Finds the layout from the deepest activated route */
  private findDeepestRouteLayout(): AppLayout {
    let child = this.route.firstChild;
    while (child?.firstChild) {
      child = child.firstChild;
    }
    return (child?.snapshot.data['layout'] as AppLayout) ?? 'private';
  }

  /**
   * On every completed navigation, resolve a readable page name, set the
   * document `<title>`, and push it into the live region so screen readers
   * announce the new page (WCAG 2.4.2 / 4.1.3).
   */
  private announceRouteChange(): void {
    const name = this.resolvePageName();
    this._titleService.setTitle(`${name} | FEC`);
    this.pageTitle.set(`${name} page`);
  }

  /**
   * Prefer an explicit `data.title` on the deepest activated route; otherwise
   * derive a name from the last non-numeric URL segment (ids are skipped).
   */
  private resolvePageName(): string {
    let child = this.route.firstChild;
    while (child?.firstChild) {
      child = child.firstChild;
    }
    const dataTitle = child?.snapshot.data['title'] as string | undefined;
    if (dataTitle) return dataTitle;

    const segments = this.router.url
      .split(/[?#]/)[0]
      .split('/')
      .filter((s) => s && !/^\d+$/.test(s));
    const last = segments.at(-1);
    if (!last) return 'Home';
    return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
